import React, { useState, useEffect } from "react";
import logo from "../assets/01pos.webp";
import { axiosInstance } from "../axiosInstance";
import { Link, useNavigate } from "react-router";
import { notify } from "../components/toastify";
import loader from "../assets/loader.webp";
import useRotatingImages from "../utility/useRotatingImages";
import CalculateIcon from "@mui/icons-material/Calculate";
import Calculator from "../utility/Calculator";
import EmailConfirmationModal from "../components/EmailConfirmationModal";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

const RECAPTCHA_SITE_KEY = "6LfOgo0sAAAAAADQv_G0IXOktWTeGNtRBqEcEQAW";

function SignupForm() {
  const currentImage = useRotatingImages(10000);
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyNumber, setCompanyNumber] = useState("");
  const [industryType, setIndustryType] = useState("");
  const [industries, setIndustries] = useState([]);
  const [, setPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculator, setCalculator] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  // Function to generate a random password
  const generatePassword = (length) => {
    length = length || 12;
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Function to get user's current location
  // const getCurrentLocation = () => {
  //   return new Promise((resolve, reject) => {
  //     if (!navigator.geolocation) {
  //       reject(new Error("Geolocation is not supported by this browser."));
  //       return;
  //     }

  //     navigator.geolocation.getCurrentPosition(
  //       async (position) => {
  //         const { latitude, longitude } = position.coords;
  //         try {
  //           // Use reverse geocoding to get address
  //           const response = await fetch(
  //             `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
  //           );
  //           const data = await response.json();

  //           if (data.locality && data.city && data.countryName) {
  //             const address = `${data.locality}, ${data.city}, ${data.countryName}`;
  //             resolve(address);
  //           } else {
  //             resolve(`${latitude}, ${longitude}`);
  //           }
  //         } catch (error) {
  //           resolve(`${latitude}, ${longitude}`);
  //         }
  //       },
  //       (error) => {
  //         reject(new Error("Unable to retrieve your location."));
  //       },
  //     );
  //   });
  // };

  // Function to handle API POST request for signup
  const postData = async (newData) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("api/onboarding", newData);

      if (response.data.error === false) {
        // Show email confirmation modal instead of toast
        setShowEmailModal(true);
      } else if (response.data.error === true) {
        notify(response?.data?.message);
      }
    } catch (err) {
      notify(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNewAddresses();
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const response = await axiosInstance.get("api/industry");
      setIndustries(response.data?.data || []);
    } catch (error) {
      console.log("Error fetching industries:", error);
    }
  };

  const setNewAddresses = async () => {
    // const locationAddress = await getCurrentLocation();
    setCompanyAddress("123 Sample Street, Lagos, Nigeria");
  };

  // Handle form submit
  const handleModalClose = () => {
    setShowEmailModal(false);
    // Redirect to login page after closing modal
    navigate("/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!industryType) {
      notify("Please select an industry type", "error");
      return;
    }

    if (!agreeToTerms) {
      notify("Please agree to the terms and privacy policy", "error");
      return;
    }

    if (!executeRecaptcha) {
      notify("reCAPTCHA not ready. Please try again.", "error");
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha("signup");
      const generatedPassword = generatePassword();
      setPassword(generatedPassword);

      const newData = {
        company_name: companyName,
        company_email: companyEmail,
        company_address: companyAddress,
        company_number: companyNumber,
        industry_type: industryType,
        password: generatedPassword,
        captcha_token: recaptchaToken,
      };

      postData(newData);
    } catch (error) {
      console.log(error);
      setLocationError(error.message);
      notify("Something went wrong. Please try again.", "error");
    }
  };

  return (
    <div
      className="relative w-full min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${currentImage})` }}
    >
      {calculator && <Calculator handleClose={setCalculator} />}
      <EmailConfirmationModal
        isOpen={showEmailModal}
        onClose={handleModalClose}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black opacity-20 z-10"></div>
      <div className="relative z-20 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
              <div className="flex justify-between items-center">
                <div>
                  <img src={logo} alt="logo" className="w-24" />
                  <h2 className="text-3xl font-semibold">
                    Let's create your account
                  </h2>
                </div>
                <CalculateIcon
                  sx={{ fontSize: "50px" }}
                  onClick={() => setCalculator(true)}
                  className="cursor-pointer"
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="companyName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Business name
                  </label>
                  <input
                    className="w-full border-2 border-gray-300 p-3 rounded-md focus:border-teal-500 focus:outline-none text-sm"
                    placeholder="business name"
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="companyEmail"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Business email
                  </label>
                  <input
                    className="w-full border-2 border-gray-300 p-3 rounded-md focus:border-teal-500 focus:outline-none text-sm"
                    placeholder="youremail@mail.com"
                    type="email"
                    id="companyEmail"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="companyNumber"
                    className="block text-sm font-medium text-gray-700"
                  >
                    WhatsApp Number{" "}
                    <span className="text-gray-400 font-normal">
                      (optional - for setup help)
                    </span>
                  </label>
                  <input
                    className="w-full border-2 border-gray-300 p-3 rounded-md focus:border-teal-500 focus:outline-none text-sm"
                    placeholder="080 1234 5678"
                    type="tel"
                    id="companyNumber"
                    value={companyNumber}
                    onChange={(e) => setCompanyNumber(e.target.value)}
                  />
                </div>

                {/* Industry type dropdown */}
                <div className="space-y-2">
                  <label
                    htmlFor="industryType"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Industry type
                  </label>
                  <select
                    id="industryType"
                    className="w-full border-2 border-gray-300 p-3 rounded-md focus:border-teal-500 focus:outline-none text-sm"
                    value={industryType}
                    onChange={(e) => setIndustryType(e.target.value)}
                    required
                  >
                    <option value="" disabled>Select industry type</option>
                    {industries.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Show location error if any */}
                {locationError && (
                  <div className="text-red-500 text-sm">{locationError}</div>
                )}
              </div>

              {/* Terms and Privacy Policy Checkbox */}
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="mt-1 flex-shrink-0"
                    required
                  />
                  <label
                    htmlFor="agreeToTerms"
                    className="text-xs text-gray-700 leading-relaxed"
                  >
                    I agree to{" "}
                    <a
                      href="https://01pos.net/ng/privacy-policy"
                      className="text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      01pos terms
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://01pos.net/ng/privacy-policy"
                      className="text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </a>
                    .
                  </label>
                </div>
                {/* <p className="text-xs text-gray-600 pl-7">
                  To create your account, we need to verify your location. This
                  is required to complete the sign-up process and ensure our
                  service is available in your area. Please allow 01POS to
                  access your location when prompted.
                </p> */}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2f345d] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#455093] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <img src={loader} alt="loading" className="w-6" />
                ) : (
                  "Create account"
                )}
              </button>

              {/* reCAPTCHA Enterprise disclaimer */}
              <p className="text-xs text-center text-gray-500">
                This site is protected by reCAPTCHA Enterprise and the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  className="text-blue-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  className="text-blue-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </p>
            </form>

            {/* Link to Login */}
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Signup() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <SignupForm />
    </GoogleReCaptchaProvider>
  );
}

export default Signup;
